import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  useTheme,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import { Close as CloseIcon, Add as AddIcon } from "@mui/icons-material";
import {
  updateExistingTask,
  fetchTasks,
  fetchEmployeeTaskDateRanges,
} from "store/features/company/projectManagement/task/taskActions";
import { fetchProjects } from "store/features/company/projectManagement/project/projectActions";
import {
  selectProjects,
  selectProjectLoading,
} from "store/features/company/projectManagement/project/projectSelectors";
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import {
  selectEmployees,
  selectEmployeeLoading,
} from "store/features/company/employee/employeeSelectors";
import {
  selectTasks,
  selectTaskLoading,
  selectEmployeeTaskDateRanges,
} from "store/features/company/projectManagement/task/taskSelectors";
import { fetchMilestones } from "store/features/company/projectManagement/milestone/milestoneActions";
import { selectMilestones } from "store/features/company/projectManagement/milestone/milestoneSelectors";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import DateRangeCalendar from "components/calendar/DateRangeCalendar";
import editTaskSvg from "assets/modals/edit.svg";
import { getTaskGroups } from "services/company/projectManagement/task/taskService";

const toInputDate = (value) => (value ? String(value).split("T")[0] : "");

const getTaskDateError = ({
  startDate,
  dueDate,
  minDate,
  maxDate,
  parentLabel,
}) => {
  if (startDate && dueDate && startDate > dueDate) {
    return "Start date cannot be after due date";
  }

  if (startDate && minDate && startDate < minDate) {
    return `Task cannot start before the ${parentLabel}`;
  }

  if (dueDate && maxDate && dueDate > maxDate) {
    return `Task cannot exceed the ${parentLabel} deadline`;
  }

  return "";
};

const EditTaskModal = ({ isOpen, onClose, task, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const rawProjects = useSelector(selectProjects);
  const projectsLoading = useSelector(selectProjectLoading);
  const employees = useSelector(selectEmployees);
  const employeesLoading = useSelector(selectEmployeeLoading);
  const currentUser = useSelector(selectCurrentUser);
  const role = getSecureLocalItem("role") || "admin";
  const userRaw = getSecureLocalItem("user");
  const storedUser = userRaw
    ? typeof userRaw === "string"
      ? JSON.parse(userRaw)
      : userRaw
    : null;
  const user = currentUser || storedUser;

  const currentUserId = user?.id || user?.user_id;
  const currentEmployee = employees.find(
    (emp) =>
      (user?.employee_id && String(emp.id) === String(user.employee_id)) ||
      (user?.id && String(emp.user_id) === String(user.id)) ||
      (user?.id && String(emp.id) === String(user.id)) ||
      (user?.email &&
        emp.email &&
        emp.email.toLowerCase() === user.email.toLowerCase()),
  );
  const currentEmployeeId =
    currentEmployee?.id || user?.employee_id || user?.id;

  const projects = useMemo(() => {
    if (!rawProjects || !Array.isArray(rawProjects)) return [];
    if (role === "Company") return rawProjects;
    if (!currentUserId && !currentEmployeeId) return rawProjects;

    return rawProjects.filter((project) => {
      // 1. Is Project Lead
      const managerId = project.project_manager_id;
      const isProjectLead =
        managerId !== undefined &&
        managerId !== null &&
        ((currentEmployeeId &&
          String(managerId) === String(currentEmployeeId)) ||
          (currentUserId && String(managerId) === String(currentUserId)));

      // 2. Is Team Member
      const teamMembers =
        project.team_members || project.teamMembers || project.members || [];

      const isTeamMember =
        Array.isArray(teamMembers) &&
        teamMembers.some(
          (memberId) =>
            (currentEmployeeId &&
              String(memberId) === String(currentEmployeeId)) ||
            (currentUserId && String(memberId) === String(currentUserId)),
        );

      return isProjectLead || isTeamMember;
    });
  }, [rawProjects, currentUserId, currentEmployeeId, role]);

  const tasks = useSelector(selectTasks);
  const tasksLoading = useSelector(selectTaskLoading);
  const milestones = useSelector(selectMilestones);
  const [parentTaskOptions, setParentTaskOptions] = useState([]);
  const [projectEmployees, setProjectEmployees] = useState([]);
  const [taskGroups, setTaskGroups] = useState([]);
  const [taskGroupsLoading, setTaskGroupsLoading] = useState(false);
  const [formData, setFormData] = useState({
    project_id: "",
    parent_task_id: "",
    task_group_id: "",
    depends_on_task_ids: [],
    milestone_id: "",
    title: "",
    description: "",
    assigned_to: [], // Changed from string to array
    task_mode: "individual", // Added task_mode
    priority: "medium",
    mode_of_assigning: "Written",

    status: "todo",
    estimated_hours: "",
    start_date: "",
    due_date: "",
    completed_at: "",
    labels: [],
  });
  const [labelInput, setLabelInput] = useState("");
  const employeeTaskDateRanges = useSelector(selectEmployeeTaskDateRanges);
  const [hasDateConflict, setHasDateConflict] = useState(false);
  const selectedProject = projects.find(
    (project) => String(project.id) === String(formData.project_id),
  );
  const selectedMilestone = milestones.find(
    (milestone) => String(milestone.id) === String(formData.milestone_id),
  );
  const taskDateBounds = selectedMilestone
    ? {
        minDate: toInputDate(selectedMilestone.start_date),
        maxDate: toInputDate(selectedMilestone.due_date),
        parentLabel: "milestone",
      }
    : {
        minDate: toInputDate(selectedProject?.start_date),
        maxDate: toInputDate(selectedProject?.end_date),
        parentLabel: "project",
      };
  const dateError = getTaskDateError({
    startDate: formData.start_date,
    dueDate: formData.due_date,
    ...taskDateBounds,
  });

  // Load initial data when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchProjects());
      dispatch(fetchEmployees());
    }
  }, [isOpen, dispatch]);

  // Populate form with task data when task changes
  useEffect(() => {
    if (task) {
      // Handle both single and multiple assignees
      let assignedTo = [];
      if (task.assignees && Array.isArray(task.assignees)) {
        // If task has assignees array (parent task)
        assignedTo = task.assignees.map((a) => a.employee_id || a);
      } else if (task.assigned_to) {
        // If task has single assignee
        assignedTo = Array.isArray(task.assigned_to)
          ? task.assigned_to
          : [task.assigned_to];
      }

      setFormData({
        project_id: task.project_id || "",
        parent_task_id: task.parent_task_id || "",
        task_group_id: task.task_group_id || "",
        depends_on_task_ids:
          task.depends_on_task_ids ||
          (task.dependencies ? task.dependencies.map((d) => d.id) : []),
        milestone_id: task.milestone_id || "",
        title: task.title || "",
        description: task.description || "",
        assigned_to: assignedTo,
        task_mode: task.task_mode || "individual",
        priority: task.priority || "medium",
        mode_of_assigning: task.mode_of_assigning || "Written",

        status: task.status || "todo",
        estimated_hours: task.estimated_hours || "",
        start_date: task.start_date ? task.start_date.split("T")[0] : "",
        due_date: task.due_date ? task.due_date.split("T")[0] : "",
        completed_at: task.completed_at ? task.completed_at.split("T")[0] : "",
        labels: task.labels || [],
      });
    }
  }, [task]);

  // Filter parent task options and fetch milestones & task groups
  useEffect(() => {
    if (formData.project_id) {
      const filteredTasks = tasks.filter(
        (t) => t.project_id == formData.project_id && t.id !== task?.id,
      );
      setParentTaskOptions(filteredTasks);
      dispatch(fetchMilestones(formData.project_id));
      setTaskGroupsLoading(true);
      getTaskGroups({ project_id: formData.project_id })
        .then((res) => {
          if (res?.data?.data) {
            setTaskGroups(res.data.data);
          }
        })
        .catch((err) =>
          console.error("Error fetching task groups in EditModal:", err),
        )
        .finally(() => setTaskGroupsLoading(false));
    } else {
      setParentTaskOptions([]);
      setTaskGroups([]);
    }
  }, [formData.project_id, tasks, task?.id, dispatch]);

  // Filter employees for the selected project
  useEffect(() => {
    if (!formData.project_id) {
      setProjectEmployees([]);
      return;
    }

    const selectedProject = projects.find(
      (p) => String(p.id) === String(formData.project_id),
    );

    if (!selectedProject) return;

    const memberIds =
      selectedProject.team_members ||
      selectedProject.teamMembers ||
      selectedProject.members ||
      [];

    const assignedIds = Array.isArray(formData.assigned_to)
      ? formData.assigned_to
      : [];

    setProjectEmployees(
      employees.filter(
        (emp) =>
          memberIds.some((memberId) => String(memberId) === String(emp.id)) ||
          assignedIds.some(
            (assignedId) => String(assignedId) === String(emp.id),
          ),
      ),
    );
  }, [formData.project_id, projects, employees, formData.assigned_to]);

  // Scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "unset";
      document.body.style.position = "unset";
      document.body.style.width = "auto";
    }
    return () => {
      document.body.style.overflow = "unset";
      document.body.style.position = "unset";
      document.body.style.width = "auto";
    };
  }, [isOpen]);

  // Check date conflicts for multiple assignees
  useEffect(() => {
    if (!isOpen || !formData.assigned_to?.length) {
      setHasDateConflict(false);
      return;
    }

    dispatch(fetchEmployeeTaskDateRanges(formData.assigned_to[0]));
  }, [isOpen, formData.assigned_to, dispatch]);

  useEffect(() => {
    if (
      !isOpen ||
      !formData.start_date ||
      !formData.due_date ||
      !employeeTaskDateRanges?.length
    ) {
      setHasDateConflict(false);
      return;
    }

    if (formData.assigned_to.length > 1) {
      setHasDateConflict(false);
      return;
    }

    const newStart = new Date(formData.start_date);
    const newEnd = new Date(formData.due_date);

    const overlap = employeeTaskDateRanges.some((range) => {
      const existingStart = new Date(range.start);
      const existingEnd = new Date(range.end);

      return newStart <= existingEnd && newEnd >= existingStart;
    });

    setHasDateConflict(overlap);
  }, [
    isOpen,
    formData.start_date,
    formData.due_date,
    formData.assigned_to,
    employeeTaskDateRanges,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "project_id" ? { milestone_id: "" } : {}),
    }));
  };

  const handleDateChange = (name, value) => {
    const formattedValue =
      value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "";
    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle multiple assignees
  const handleAssigneeChange = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      assigned_to: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleAddLabel = () => {
    if (labelInput.trim() && !formData.labels.includes(labelInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        labels: [...prev.labels, labelInput.trim()],
      }));
      setLabelInput("");
    }
  };

  const handleRemoveLabel = (labelToRemove) => {
    setFormData((prev) => ({
      ...prev,
      labels: prev.labels.filter((label) => label !== labelToRemove),
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddLabel();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.project_id) {
      return;
    }
    if (dateError) {
      return;
    }

    setLoading(true);

    // Prepare assignees - send array
    const assignedTo = Array.isArray(formData.assigned_to)
      ? formData.assigned_to
      : formData.assigned_to
      ? [formData.assigned_to]
      : [];

    const submitData = {
      project_id: parseInt(formData.project_id),
      title: formData.title,
      description: formData.description,
      assigned_to: assignedTo.length > 0 ? assignedTo : undefined,
      task_mode: formData.task_mode,
      priority: formData.priority,
      mode_of_assigning: formData.mode_of_assigning || "Written",

      estimated_hours: formData.estimated_hours
        ? parseFloat(formData.estimated_hours)
        : 0,
      start_date: formData.start_date,
      due_date: formData.due_date,
      labels: formData.labels,
    };

    // Only include optional fields if they have values
    if (formData.parent_task_id) {
      submitData.parent_task_id = parseInt(formData.parent_task_id);
    }
    if (formData.milestone_id) {
      submitData.milestone_id = parseInt(formData.milestone_id);
    }
    if (formData.completed_at) {
      submitData.completed_at = formData.completed_at;
    }

    const result = await dispatch(updateExistingTask(task.id, submitData));

    if (result.success) {
      onSuccess();
      onClose();
    }
    setLoading(false);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "text.secondary",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "primary.main",
    },
  };

  return (
    <AnimatePresence>
      {isOpen && task && (
        <>
          <style>
            {`
              .MuiPopover-root { z-index: 1400 !important; }
              .MuiPopper-root { z-index: 1400 !important; }
              .MuiMenu-root { z-index: 1400 !important; }
            `}
          </style>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(252, 226, 181, 0.14)"
                  : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "800px",
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={editTaskSvg}
                      alt="Edit Task"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        transition: "all 0.3s",
                        "&:hover": {
                          filter: "drop-shadow(0 4px 8px rgba(245,158,11,0.3))",
                        },
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: "bold",
                        color: "text.primary",
                      }}
                    >
                      Edit Task
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Update task information
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  color: "text.secondary",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f59e0b",
                    color: "#ffffff",
                    transform: "scale(1.05)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            <form onSubmit={handleSubmit}>
              <Box sx={{ p: 3 }}>
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      select
                      label="Project"
                      name="project_id"
                      value={formData.project_id}
                      onChange={handleChange}
                      required
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                      disabled={projectsLoading}
                    >
                      <MenuItem value="">
                        <em>Select a project</em>
                      </MenuItem>
                      {projects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.name} ({project.code})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>

                  <TextField
                    fullWidth
                    label="Task Title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="Enter task title"
                    sx={inputSx}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="Brief description of the task"
                    sx={inputSx}
                    InputLabelProps={{ shrink: true }}
                  />

                  {/* Multi-select for Assignees */}
                  <FormControl fullWidth sx={inputSx}>
                    <InputLabel id="edit-assignee-select-label">
                      Assign To
                    </InputLabel>
                    <Select
                      labelId="edit-assignee-select-label"
                      id="edit-assignee-select"
                      multiple
                      name="assigned_to"
                      value={formData.assigned_to}
                      onChange={handleAssigneeChange}
                      input={<OutlinedInput label="Assign To" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => {
                            const employee = employees.find(
                              (emp) => emp.id === value,
                            );
                            return (
                              <Chip
                                key={value}
                                label={
                                  employee
                                    ? `${employee.first_name} ${employee.last_name}`
                                    : value
                                }
                                size="small"
                                sx={{
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "#334155"
                                      : "#f3f4f6",
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                      disabled={employeesLoading}
                    >
                      {projectEmployees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_code})
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select multiple employees for team tasks
                    </FormHelperText>
                  </FormControl>

                  {/* Task Mode Selection */}
                  <FormControl fullWidth sx={inputSx}>
                    <InputLabel id="edit-task-mode-label">Task Mode</InputLabel>
                    <Select
                      labelId="edit-task-mode-label"
                      id="edit-task-mode"
                      name="task_mode"
                      value={formData.task_mode}
                      onChange={handleChange}
                      label="Task Mode"
                    >
                      <MenuItem value="individual">
                        Individual - Each person completes independently
                      </MenuItem>
                      <MenuItem value="shared">
                        Shared - One completion completes for all
                      </MenuItem>
                    </Select>
                    <FormHelperText>
                      {formData.task_mode === "shared"
                        ? "When one person completes, the task is done for everyone"
                        : "Each person tracks their own progress independently"}
                    </FormHelperText>
                  </FormControl>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Estimated Hours"
                      name="estimated_hours"
                      type="number"
                      value={formData.estimated_hours}
                      onChange={handleNumberChange}
                      placeholder="10"
                      helperText="Estimated hours to complete"
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    />

                    <TextField
                      fullWidth
                      select
                      label="Priority"
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="urgent">Urgent</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                    </TextField>
                    {/* Mode of Assigning */}
                    <TextField
                      fullWidth
                      select
                      label="Mode of Assigning"
                      name="mode_of_assigning"
                      value={formData.mode_of_assigning}
                      onChange={handleChange}
                      sx={inputSx}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="Email">Email</MenuItem>
                      <MenuItem value="Written">Written</MenuItem>
                      <MenuItem value="Verbal">Verbal</MenuItem>
                      <MenuItem value="Meeting Resolutions">
                        Meeting Resolutions
                      </MenuItem>
                      <MenuItem value="Regular Assignments">
                        Regular Assignments
                      </MenuItem>
                      <MenuItem value="Self">Self</MenuItem>
                    </TextField>
                  </Stack>

                  <TextField
                    fullWidth
                    select
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    sx={inputSx}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="todo">To Do</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="review">Review</MenuItem>
                    <MenuItem value="done">Done</MenuItem>
                    <MenuItem value="blocked">Blocked</MenuItem>
                    <MenuItem value="paused">Paused</MenuItem>
                  </TextField>

                  <DateRangeCalendar
                    startDate={formData.start_date}
                    dueDate={formData.due_date}
                    onChange={({ startDate, endDate }) => {
                      setFormData((prev) => ({
                        ...prev,
                        start_date: startDate,
                        due_date: endDate,
                      }));
                    }}
                    minDate={taskDateBounds.minDate}
                    maxDate={taskDateBounds.maxDate}
                    label="Task Timeline (Date Range)"
                    startLabel="Start Date"
                    endLabel="Due Date"
                    error={dateError}
                    accentColor="#f59e0b"
                  />

                  {formData.status === "done" && (
                    <DatePicker
                      label="Completed Date"
                      value={
                        formData.completed_at
                          ? dayjs(formData.completed_at)
                          : null
                      }
                      onChange={(newValue) =>
                        handleDateChange("completed_at", newValue)
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: inputSx,
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  )}

                  {hasDateConflict && (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: "12px",
                        border: "1px solid",
                        borderColor: "#f59e0b",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(245,158,11,0.08)"
                            : "#fffbeb",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "primary.main",
                          fontWeight: 600,
                        }}
                      >
                        Assigned member already have tasks within the selected
                        date range.
                      </Typography>
                    </Box>
                  )}

                  {/* Labels Section */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        color: "text.secondary",
                        fontWeight: 500,
                      }}
                    >
                      Labels
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        fullWidth
                        placeholder="Add labels..."
                        value={labelInput}
                        onChange={(e) => setLabelInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#1e293b"
                                : "#f9fafb",
                          },
                        }}
                      />
                      <Button
                        onClick={handleAddLabel}
                        variant="outlined"
                        sx={{
                          borderRadius: "12px",
                          borderColor: "#f59e0b",
                          color: "primary.main",
                          "&:hover": {
                            borderColor: "#d97706",
                            backgroundColor: "rgba(245,158,11,0.1)",
                          },
                        }}
                      >
                        <AddIcon />
                      </Button>
                    </Stack>
                    {formData.labels.length > 0 && (
                      <Stack
                        direction="row"
                        sx={{
                          mt: 1,
                          flexWrap: "wrap",
                          gap: 1,
                          overflowX: "hidden",
                        }}
                      >
                        {formData.labels.map((label, index) => (
                          <Chip
                            key={index}
                            label={label}
                            onDelete={() => handleRemoveLabel(label)}
                            size="small"
                            sx={{
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#334155"
                                  : "#f3f4f6",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#374151",
                              "& .MuiChip-deleteIcon": {
                                color: "primary.main",
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor:
                      theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                    color:
                      theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                    "&:hover": {
                      borderColor: "#f59e0b",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(245,158,11,0.1)"
                          : "#fffbeb",
                    },
                  }}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    loading ||
                    !formData.title.trim() ||
                    !formData.project_id ||
                    Boolean(dateError)
                  }
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                    boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                      transform: "scale(1.02)",
                      boxShadow: "0 20px 25px -5px rgba(245, 158, 11, 0.3)",
                    },
                    "&:disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Update Task"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditTaskModal;
